import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromoteStudentResult, OrgPromotionApi } from '@/services/org-promotion.api';
import { PromotionResult, PromotionStatus } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Select } from '@/components/ui/Input';

interface RowDecision {
  result: PromotionResult;
  status: PromotionStatus;
}

/**
 * Org-tier Promote Student — single school, but the School itself is
 * picked inline as the first field instead of switching into a school
 * session first (the enterSchool() deep-link this replaced). Otherwise a
 * straight mirror of the tenant-tier PromotionPage: real class/section/
 * year ids are unambiguous once a school is picked (unlike Bulk
 * Promotion's name-matching, which exists precisely because MULTIPLE
 * schools are in play there). Calls the SAME StudentsService.promote()
 * every other promotion entry point uses.
 */
export function OrgPromoteStudentPage() {
  const qc = useQueryClient();

  const [schoolId, setSchoolId] = useState('');
  const [fromClassId, setFromClassId] = useState('');
  const [fromSectionId, setFromSectionId] = useState('');
  const [toClassId, setToClassId] = useState('');
  const [toSectionId, setToSectionId] = useState('');
  const [newAcademicYearId, setNewAcademicYearId] = useState('');
  const [decisions, setDecisions] = useState<Record<string, RowDecision>>({});
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OrgPromoteStudentResult | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-promote-student'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['org-promote-student-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });
  const fromSections = classes.find((c) => c.id === fromClassId)?.sections ?? [];
  const toSections = classes.find((c) => c.id === toClassId)?.sections ?? [];

  const { data: years = [] } = useQuery({
    queryKey: ['org-promote-student-years', schoolId],
    queryFn: () => OrgPromotionApi.academicYearsForSchool(schoolId),
    enabled: !!schoolId,
  });

  // Changing schools invalidates every downstream pick — ids from one
  // school's schema mean nothing in another's.
  useEffect(() => {
    setFromClassId('');
    setFromSectionId('');
    setToClassId('');
    setToSectionId('');
    setNewAcademicYearId('');
    setResult(null);
    setError(null);
  }, [schoolId]);

  const { data: students = [], isFetching: loadingStudents } = useQuery({
    queryKey: ['org-promote-student-roster', schoolId, fromSectionId],
    queryFn: () => OrgPromotionApi.studentsForSection(schoolId, fromSectionId),
    enabled: !!schoolId && !!fromSectionId,
  });

  useEffect(() => {
    // Default everyone to Pass + Continue — the admin only needs to touch exceptions (repeats/leavers).
    const next: Record<string, RowDecision> = {};
    students.forEach((s) => {
      next[s.id] = decisions[s.id] ?? { result: 'pass', status: 'continue' };
    });
    setDecisions(next);
    setResult(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromSectionId, students]);

  const setDecision = (studentId: string, patch: Partial<RowDecision>) =>
    setDecisions((prev) => ({ ...prev, [studentId]: { ...prev[studentId], ...patch } }));

  const outcomeLabel = (d: RowDecision) => {
    if (d.status === 'left') return 'Leaves';
    return d.result === 'pass' ? 'Promoted' : 'Repeats (same section)';
  };

  const promote = useMutation({
    mutationFn: () =>
      OrgPromotionApi.promoteStudent({
        schoolId,
        fromSectionId,
        toSectionId,
        newAcademicYearId,
        students: students.map((s) => ({ studentId: s.id, ...decisions[s.id] })),
      }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['org-promote-student-roster'] });
      qc.invalidateQueries({ queryKey: ['org-promotion-log'] });
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Promotion failed'),
  });

  const needsToSection = students.some((s) => decisions[s.id]?.result === 'pass' && decisions[s.id]?.status === 'continue');
  const ready =
    !!schoolId && !!fromSectionId && !!newAcademicYearId && (!needsToSection || !!toSectionId) && students.length > 0;

  return (
    <div>
      <PageHeader
        title="Promote Student"
        description="Single-school, year-end promotion — pick a school, then move its students to a new academic year. Fees are untouched."
      />

      {error && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}
      {result && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          Processed {result.processed} student(s) —{' '}
          {result.students.filter((s) => s.outcome === 'promoted').length} promoted,{' '}
          {result.students.filter((s) => s.outcome === 'repeating').length} repeating,{' '}
          {result.students.filter((s) => s.outcome === 'left').length} left.
        </div>
      )}

      <div className="mb-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="w-72">
          <Field label="School" required>
            <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
              <option value="">Select</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {schoolId && (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_auto_1fr]">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Class" required>
                  <Select
                    value={fromClassId}
                    onChange={(e) => {
                      setFromClassId(e.target.value);
                      setFromSectionId('');
                    }}
                  >
                    <option value="">Select</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Section" required>
                  <Select value={fromSectionId} onChange={(e) => setFromSectionId(e.target.value)} disabled={!fromClassId}>
                    <option value="">Select</option>
                    {fromSections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className="hidden items-center justify-center md:flex">
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Promote to Class" required={needsToSection}>
                  <Select
                    value={toClassId}
                    onChange={(e) => {
                      setToClassId(e.target.value);
                      setToSectionId('');
                    }}
                  >
                    <option value="">Select</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Promote to Section" required={needsToSection}>
                  <Select value={toSectionId} onChange={(e) => setToSectionId(e.target.value)} disabled={!toClassId}>
                    <option value="">Select</option>
                    {toSections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
            </div>

            <div className="w-64">
              <Field label="Promote In (New Academic Year)" required>
                <Select value={newAcademicYearId} onChange={(e) => setNewAcademicYearId(e.target.value)}>
                  <option value="">Select</option>
                  {years.map((y) => (
                    <option key={y.id} value={y.id}>
                      {y.name}
                      {y.isCurrent ? ' (current)' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </>
        )}
      </div>

      {schoolId && fromSectionId && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-700">Students {loadingStudents ? '' : `(${students.length})`}</h3>
          </div>
          {loadingStudents ? (
            <div className="p-6 text-sm text-slate-400">Loading…</div>
          ) : students.length === 0 ? (
            <div className="p-6 text-sm text-slate-400">No students currently in this section.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase text-slate-400">
                  <th className="px-4 py-2">Admission #</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Roll #</th>
                  <th className="px-4 py-2">Result</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => {
                  const d = decisions[s.id] ?? { result: 'pass', status: 'continue' };
                  return (
                    <tr key={s.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2">{s.admissionNo}</td>
                      <td className="px-4 py-2">{s.name ?? <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-2">{s.rollNumber ?? '—'}</td>
                      <td className="px-4 py-2">
                        <Select
                          className="!w-28"
                          value={d.result}
                          onChange={(e) => setDecision(s.id, { result: e.target.value as PromotionResult })}
                        >
                          <option value="pass">Pass</option>
                          <option value="fail">Fail</option>
                        </Select>
                      </td>
                      <td className="px-4 py-2">
                        <Select
                          className="!w-28"
                          value={d.status}
                          onChange={(e) => setDecision(s.id, { status: e.target.value as PromotionStatus })}
                        >
                          <option value="continue">Continue</option>
                          <option value="left">Leave</option>
                        </Select>
                      </td>
                      <td className="px-4 py-2 text-slate-500">{outcomeLabel(d)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={!ready || promote.isPending}
          onClick={() => {
            setError(null);
            setResult(null);
            promote.mutate();
          }}
        >
          {promote.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
          Promote {students.length > 0 ? `(${students.length})` : ''}
        </button>
      </div>
    </div>
  );
}
